/* ================================================================== *
 * Consultant Hub — shared shapes + period helpers (client + server).
 *
 * The hub reports sales performance company-wide and per territory
 * (territory = the customer's state → `TERRITORIES` in lib/territories.ts)
 * at monthly, quarterly, and annual grain on the fiscal calendar (Apr–Mar).
 *
 * Definitions (same as the Executive dashboard + Territories tracker):
 *   Opportunities = deals with a free-quote-form date in the period (Sales + Production).
 *   Closed deals  = Production deals with an order date in the period, excl. Returned/Refunded.
 *   Gross / Net   = SUM(amount_in_home_currency) / SUM(net_price) of closed deals.
 *   Fiscal YTD K  = referral closes (Friend + Affiliate) from April 1 through the
 *                   end of the period ÷ all closes over the same dates last year.
 *   Rolling K     = the same ratio over the trailing 12 months ending with the period.
 * ================================================================== */

/* ---- K goal (Austin, 2026-10-19) ----
 * Last fiscal year's full-year K + K_GOAL_LIFT (same target all year). */
export const K_GOAL_LIFT = 0.05;

/* ---- Territory guardrails (Austin, 2026-10-22) ----
 * Every territory page tracks these four. K's guardrail is the K goal above
 * (last fiscal year's full-year K + K_GOAL_LIFT). */
export const GUARDRAILS = {
  /** Gross profit margin floor: (net − flat per-ring cost) ÷ gross. */
  minGpm: 0.4,
  /** Close rate floor: closed ÷ opportunities (60% → 57% per Austin, 2026-10-30). */
  minCloseRate: 0.57,
  /** Opportunity ceiling per consultant per month. */
  maxOppsPerConsultant: 80,
} as const;

export type Grain = "month" | "quarter" | "year";

export interface HubPeriod {
  key: string; // "2026-10" | "FY27-Q3" | "FY27"
  label: string; // "Oct 2026" | "Q3 (Oct–Dec)" | "FY26/27"
  months: string[]; // YYYY-MM members
  status: "past" | "current" | "future";
}

interface SourceCell {
  count: number;
  gross: number;
}
/** The numbers in K's formula over some stretch of dates: K = relational ÷ lyClosed. */
export interface KParts {
  /** Friend & family closes. */
  friend: number;
  /** Affiliate closes. */
  affiliate: number;
  /** friend + affiliate — K's numerator. */
  relational: number;
  /** All closes over the same dates last year — K's denominator. */
  lyClosed: number;
}

export interface PeriodMetrics {
  opps: number;
  closed: number;
  gross: number;
  net: number;
  closeRate: number | null;
  /** Gross profit margin: (net − flat per-ring cost) ÷ gross — same formula as commission GPM. */
  gpm: number | null;
  /** Opportunities per elapsed month in the period (a month's own count at monthly grain). */
  oppsPerMonth: number;
  avgSale: number | null;
  /** Fiscal YTD K as of the end of the period (to today when in progress). */
  k: number | null;
  /** Rolling K: trailing 12 months ending with the period. */
  k12: number | null;
  referralCloses: number;
  /** K for this period alone: its referral closes ÷ all closes in the same period last year. */
  periodK: number | null;
  /** The numbers behind periodK — this period's own counts, not running totals. */
  periodParts: KParts;
  /** The numbers behind Fiscal YTD K (April 1 → end of period): k = relational ÷ lyClosed. */
  kParts: KParts | null;
  oppsBySource: Record<string, number>;
  closedBySource: Record<string, SourceCell>;
  /** Same period one year earlier (to the same day when the period is in progress). */
  prior: { opps: number; closed: number; gross: number; net: number };
  /** The whole period one year earlier (for an in-progress period: the full period, not to date). */
  priorFull: { opps: number; closed: number; gross: number; net: number };
  /** Last fiscal year's full-year K — the base for the K goal (same for every period of a year). */
  priorK: number | null;
}

export interface HubScope {
  key: string; // "all" | "division-east" | territory key | "unassigned"
  kind: "company" | "division" | "territory" | "unassigned";
  label: string;
  consultant?: string;
  division?: string;
  divisionHead?: string;
  states?: string[];
  /** Division scopes: the territory keys inside it, in display order. */
  territories?: string[];
}

/** Scope key for a division ("East" → "division-east"); also its page slug suffix. */
export function divisionScopeKey(division: string): string {
  return `division-${division.toLowerCase()}`;
}

export interface HubResponse {
  grain: Grain;
  fy: number;
  fyLabel: string;
  fyOptions: { fy: number; label: string }[];
  /** Product filter applied to every number ("all" | "engagement" | "wedding"). */
  product: string;
  productOptions: { key: string; label: string }[];
  periods: HubPeriod[];
  buckets: { key: string; label: string }[];
  scopes: HubScope[];
  /** scope key → one entry per period. Future periods hold only last-year figures (for goals). */
  data: Record<string, (PeriodMetrics | null)[]>;
  /** scope key → whole fiscal year: actuals to date, priorFull = last year's full year (month/quarter grain only). */
  totals: Record<string, PeriodMetrics | null>;
  meta: { locatedPct: number; asOf: string; currentPeriodKey: string | null };
}

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTH_ABBR[Number(m) - 1]} ${y}`;
}

function fyShort(fy: number) {
  return `FY${String(fy).slice(-2)}`;
}

/** YYYY-MM of today (local server time). */
export function currentYm(today = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

/** Shift a YYYY-MM by n months. */
export function shiftYm(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

function fyMonths(fy: number): string[] {
  return Array.from({ length: 12 }, (_, i) => shiftYm(`${fy - 1}-04`, i));
}

function statusOf(months: string[], nowYm: string): HubPeriod["status"] {
  const first = months[0] ?? "";
  const last = months[months.length - 1] ?? "";
  if (first > nowYm) return "future";
  if (last < nowYm) return "past";
  return "current";
}

const QUARTER_SPANS = ["Apr–Jun", "Jul–Sep", "Oct–Dec", "Jan–Mar"];

/** Periods (columns) for a grain. Annual grain shows the 5 fiscal years ending with `fy`. */
export function buildPeriods(grain: Grain, fy: number, nowYm: string): HubPeriod[] {
  if (grain === "month") {
    return fyMonths(fy).map((m) => ({
      key: m,
      label: monthLabel(m),
      months: [m],
      status: statusOf([m], nowYm),
    }));
  }
  if (grain === "quarter") {
    const ms = fyMonths(fy);
    return [0, 1, 2, 3].map((q) => {
      const months = ms.slice(q * 3, q * 3 + 3);
      return {
        key: `${fyShort(fy)}-Q${q + 1}`,
        label: `Q${q + 1} (${QUARTER_SPANS[q]})`,
        months,
        status: statusOf(months, nowYm),
      };
    });
  }
  return [4, 3, 2, 1, 0].map((back) => {
    const y = fy - back;
    const months = fyMonths(y);
    return {
      key: fyShort(y),
      label: `FY${String(y - 1).slice(-2)}/${String(y).slice(-2)}`,
      months,
      status: statusOf(months, nowYm),
    };
  });
}
